import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import RoleSelect from './pages/RoleSelect';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import BrowseCreatives from './pages/BrowseCreatives';
import CreativeProfile from './pages/CreativeProfile';
import BusinessProfile from './pages/BusinessProfile';
import AIMatch from './pages/AIMatch';
import Dashboard from './pages/Dashboard';
import Bookings from './pages/Bookings';
import Messages from './pages/Messages';
import EditProfile from './pages/EditProfile';
import EditBusinessProfile from './pages/EditBusinessProfile';
import Projects from './pages/Projects';
import ProjectApplications from './pages/ProjectApplications';
import AvailabilityCalendar from './pages/AvailabilityCalendar';
import Agreement from './pages/Agreement';
import Deliverables from './pages/Deliverables';
import AdminDashboard from './pages/AdminDashboard';
import TermsCreative from './pages/TermsCreative';
import TermsClient from './pages/TermsClient';
import Privacy from './pages/Privacy';
import HelpCenter from './pages/HelpCenter';
import RealEstate from './pages/RealEstate';
import Events from './pages/Events';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Feedback from './pages/Feedback';
import AppLayout from './components/AppLayout';
import SetupPayments from './pages/SetupPayments';

import './App.css';

import { API } from './utils';

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      localStorage.removeItem('token');
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    try {
      const savedToken = token || localStorage.getItem('token');
      if (savedToken) {
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${savedToken}` }
        });
        setUser(response.data);
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    setUser(response.data);
    setToken(response.data.token);
    localStorage.setItem('token', response.data.token);
    return response.data;
  };

  const register = async (email, password, name, role, company_name = '') => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role, company_name });
    setUser(response.data);
    setToken(response.data.token);
    localStorage.setItem('token', response.data.token);
    return response.data;
  };

  const googleLogin = async (credential) => {
    const response = await axios.post(`${API}/auth/google`, { credential }, { withCredentials: true });
    setUser(response.data);
    setToken(response.data.token);
    localStorage.setItem('token', response.data.token);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      // Logout API call may fail if server is down, but we still want to clear local state
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
  };

  const setRole = async (role) => {
    const response = await axios.post(`${API}/auth/set-role`, { role }, {
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    });
    setUser(prev => ({ ...prev, role }));
    return response.data;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, googleLogin, register, logout, setRole, token, checkAuth, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback - handles Google OAuth
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, checkAuth } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');
      
      if (!sessionId) {
        navigate('/login');
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
        setUser(response.data);
        
        if (response.data.role === 'pending') {
          navigate('/select-role', { state: { user: response.data } });
        } else {
          navigate('/dashboard', { state: { user: response.data } });
        }
      } catch (error) {
        toast.error('Authentication failed');
        navigate('/login');
      }
    };

    processSession();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-pulse text-muted-foreground">Authenticating...</div>
    </div>
  );
};

// Public Route - accessible without login, but shows AppLayout if logged in
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (user && user.role !== 'pending') {
    return <AppLayout>{children}</AppLayout>;
  }

  return children;
};

// Protected Route with Layout
const ProtectedRoute = ({ children, allowedRoles, noLayout = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (user.role === 'pending') {
    return <Navigate to="/select-role" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Wrap with AppLayout unless noLayout is specified
  if (noLayout) {
    return children;
  }

  return <AppLayout>{children}</AppLayout>;
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check for session_id in hash - must be synchronous during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/select-role" element={<RoleSelect />} />
      <Route path="/setup-payments" element={<SetupPayments />} />
      <Route path="/terms/creative" element={<TermsCreative />} />
      <Route path="/terms/client" element={<TermsClient />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/help" element={<HelpCenter />} />
      <Route path="/real-estate" element={<RealEstate />} />
      <Route path="/events" element={<Events />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/feedback" element={
        <ProtectedRoute>
          <Feedback />
        </ProtectedRoute>
      } />
      <Route path="/photographers" element={
        <PublicRoute>
          <BrowseCreatives />
        </PublicRoute>
      } />
      <Route path="/photographers/:profileId" element={
        <PublicRoute>
          <CreativeProfile />
        </PublicRoute>
      } />
      <Route path="/business/:userId" element={
        <ProtectedRoute>
          <BusinessProfile />
        </ProtectedRoute>
      } />
      <Route path="/projects" element={
        <ProtectedRoute>
          <Projects />
        </ProtectedRoute>
      } />
      <Route path="/projects/:projectId/applications" element={
        <ProtectedRoute allowedRoles={['business']}>
          <ProjectApplications />
        </ProtectedRoute>
      } />
      <Route path="/ai-match" element={
        <ProtectedRoute allowedRoles={['business']}>
          <AIMatch />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/bookings" element={
        <ProtectedRoute>
          <Bookings />
        </ProtectedRoute>
      } />
      <Route path="/bookings/:bookingId/agreement" element={
        <ProtectedRoute>
          <Agreement />
        </ProtectedRoute>
      } />
      <Route path="/deliverables/:bookingId" element={
        <ProtectedRoute>
          <Deliverables />
        </ProtectedRoute>
      } />
      <Route path="/messages" element={
        <ProtectedRoute>
          <Messages />
        </ProtectedRoute>
      } />
      <Route path="/messages/:partnerId" element={
        <ProtectedRoute>
          <Messages />
        </ProtectedRoute>
      } />
      <Route path="/edit-profile" element={
        <ProtectedRoute allowedRoles={['creative']}>
          <EditProfile />
        </ProtectedRoute>
      } />
      <Route path="/edit-business-profile" element={
        <ProtectedRoute allowedRoles={['business']}>
          <EditBusinessProfile />
        </ProtectedRoute>
      } />
      <Route path="/availability" element={
        <ProtectedRoute allowedRoles={['creative']}>
          <AvailabilityCalendar />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <HelmetProvider>
      <GoogleOAuthProvider clientId="436928045997-jnvj1mh2sejffkuen4o7s9j3qnvjrnpt.apps.googleusercontent.com">
        <BrowserRouter>
          <AuthProvider>
            <Toaster position="top-right" richColors />
            <AppRouter />
          </AuthProvider>
        </BrowserRouter>
      </GoogleOAuthProvider>
    </HelmetProvider>
  );
}

export default App;
